import React, { Component, ReactNode } from 'react';
import { connect } from 'react-redux';
import {
  BrowserRouter,
  Switch,
  withRouter,
  Route,
  Redirect,
} from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import Login from './modules/public/login/Login';
import NavRoutes from './constants/NavRoutes';
import NotFound from './components/notfound/NotFound';
import Home from './modules/home/Home';
import {
  PublicRoute,
  PrivateRoute,
  PatientPublicRoute,
  PatientPrivateRoute,
} from './components/route/AuthRoute';
import AuthUtils from './utils/AuthUtils';
import {
  _initApp,
  _initPatientApp,
} from '../../shared/src/store/actions/AuthActions';
import AppLoader from './components/apploader/AppLoader';
import { axios } from '../../shared/src/axios';
import './style/style.scss';
import { BASE_ROUTE, PATIENT_BASE_ROUTE, PATIENT_FORMS_BASE_ROUTE } from './constants/BaseRoute';
import InitSharedRepo from '../../shared/index';
import UploadCards from './modules/uploadCards/UploadCards';
import PatientLogin from './modules/public/patientLogin/PatientLogin';
import SuccessPage from './modules/successPage/SuccessPage';
import PatientRedirect from './components/patientRedirect/PatientRedirect';
import PatientLoginError from './modules/public/patientLogin/PatientLoginError';
import DownloadPatientForm from './modules/surgeryForms/DownloadForm';
class App extends Component<any, any> {
  constructor(props: any) {
    super(props);
    this.state = {
      loading: true,
    };
    this.handleAppEntry = this.handleAppEntry.bind(this);
    this.handlePatientAppEntry = this.handlePatientAppEntry.bind(this);
    this.getAuthStatusFromStorage = this.getAuthStatusFromStorage.bind(this);
    this.getPatientAuthStatusFromStorage = this.getPatientAuthStatusFromStorage.bind(
      this
    );
    this.plugInterceptors = this.plugInterceptors.bind(this);
  }

  componentDidMount() {
    this.handleAppEntry();
    this.handlePatientAppEntry();
    this.plugInterceptors();
  }

  plugInterceptors(): void {
    // Request interceptor
    axios.interceptors.request.use(
      function (config) {
        // Do something before request is sent
        return config;
      },
      function (error) {
        // Do something with request error
        return Promise.reject(error);
      }
    );

    // Response interceptor
    axios.interceptors.response.use(
      function (response) {
        // Any status code that lie within the range of 2xx cause this function to trigger
        // Do something with response data
        return response;
      },
      function (error) {
        // Any status codes that falls outside the range of 2xx cause this function to trigger
        // Do something with response error
        return Promise.reject(error);
      }
    );
  }

  // Token can be verified here before preparing the return object
  getAuthStatusFromStorage() {
    const token = AuthUtils.getAuthToken();
    return { status: token ? true : false, authToken: token };
  }

  getPatientAuthStatusFromStorage() {
    const token = AuthUtils.getPatientAuthToken();
    return { status: token ? true : false, patientAuthToken: token };
  }

  async handleAppEntry() {
    const authStatus = this.getAuthStatusFromStorage();
    const authToken: any = authStatus.status ? authStatus.authToken : null;
    this.props._initApp({ authToken });
    InitSharedRepo.initAfterAuth(authToken);
    // If we want redirection on some specific route, first let it happen before changeing loading
    // this.props.history.replace('/404')
    setTimeout(() => {
      this.setState({ loading: false });
    }, 1000);
  }

  async handlePatientAppEntry() {
    const patientAuthStatus = this.getPatientAuthStatusFromStorage();
    const patientAuthToken: any = patientAuthStatus.status
      ? patientAuthStatus.patientAuthToken
      : null;
    this.props._initPatientApp({ patientAuthToken });
    patientAuthToken && InitSharedRepo.initAfterPatientAuth(patientAuthToken);
    // If we want redirection on some specific route, first let it happen before changeing loading
    // this.props.history.replace('/404')
    setTimeout(() => {
      this.setState({ loading: false });
    }, 1000);
  }

  render(): ReactNode {
    if (this.state.loading) {
      return <AppLoader />;
    }
    const { authToken } = this.props;
    const isUserAuthenticated = authToken ? true : false;
    const { patientAuthToken } = this.props;
    const isPatientAuthenticated = patientAuthToken ? true : false;
    return (
      <BrowserRouter>
        <Switch>
          <Redirect
            from="/"
            to={`/${BASE_ROUTE}/${NavRoutes.PUBLIC.LOGIN}`}
            exact
          />
          <PublicRoute
            path={`/${BASE_ROUTE}/${NavRoutes.PUBLIC.LOGIN}`}
            component={Login}
            isUserAuthenticated={isUserAuthenticated}
          />
          <PrivateRoute
            path={`/${BASE_ROUTE}/${NavRoutes.PRIVATE.HOME}`}
            component={Home}
            isUserAuthenticated={isUserAuthenticated}
          />
          <Redirect
            from={`/${BASE_ROUTE}/`}
            to={`/${BASE_ROUTE}/${NavRoutes.PUBLIC.LOGIN}`}
            exact
          />

          <PatientPublicRoute
            path={`/${PATIENT_BASE_ROUTE}/${NavRoutes.PUBLIC.LOGIN}/:code`}
            component={PatientLogin}
            isUserAuthenticated={isPatientAuthenticated}
          />
          <PatientPrivateRoute
            path={`/${PATIENT_BASE_ROUTE}/${NavRoutes.PRIVATE.PATIENT.DOCUMENTS}/${NavRoutes.PRIVATE.PATIENT.UPLOAD_CARDS}`}
            component={UploadCards}
            isUserAuthenticated={isPatientAuthenticated}
          />
          <PatientPublicRoute
            path={`/${PATIENT_BASE_ROUTE}/${NavRoutes.PRIVATE.PATIENT.DOCUMENTS}/${NavRoutes.PRIVATE.PATIENT.SUCCESS}`}
            component={SuccessPage}
            isUserAuthenticated={isPatientAuthenticated}
          />

          <Route path={`/${PATIENT_FORMS_BASE_ROUTE}/${NavRoutes.PUBLIC.PATIENT_FORMS.DOWNLOAD_FORM}/:code`} component={DownloadPatientForm} />

          <Route path={`/${NavRoutes.PUBLIC[404]}`} component={NotFound} />
          <Route
            path={`/${PATIENT_BASE_ROUTE}/${NavRoutes.PUBLIC[404]}`}
            component={PatientRedirect}
          />
          <Route
            path={`/${PATIENT_BASE_ROUTE}/${NavRoutes.PUBLIC.PATIENT_LOGIN_ERROR}`}
            component={PatientLoginError}
          />
          <Redirect to={`/${NavRoutes.PUBLIC[404]}`} />
        </Switch>
      </BrowserRouter>
    );
  }
}

type StateObj = {
  auth: {
    authToken?: string;
    patientAuthToken?: string;
  };
};

function mapStateToProps(state: StateObj) {
  // Properties defined here (read from state) will be available as props in the component
  return {
    authToken: state.auth.authToken,
    patientAuthToken: state.auth.patientAuthToken,
  };
}

export default connect(mapStateToProps, {
  _initApp,
  _initPatientApp,
  // Binding this dispatcher to be available in props in the component
})(withRouter(App));
